import { App as AntApp, ConfigProvider, notification } from "antd";
import { onMessage } from "firebase/messaging";
import { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
// import notificationSound from "./assets/audio/notification-sound.wav";
import { useKeycloak } from "@react-keycloak/web";
import NotificationIcon from "./assets/icons/NotificationIcon";
import Loader from "./components/common/Loader";
import { generateToken, messaging } from "./helpers/firebaseConfig";
import useApp from "./hooks/useApp";
import useAppBoot from "./hooks/useAppBoot";
import useAuthChecked from "./hooks/useAuthChecked";
import SidebarLayout from "./layouts/SidebarLayout";
import AuthMiddleware from "./middleware/AuthMiddleware";
import { authRouters, publicRouters } from "./routers/routers";
import { addMobileTokenAsync } from "./store/features/auth/loginAPI";
import { useAppDispatch, useAppSelector } from "./store/store";
import { stringToArray } from "./utils/array";

type MSGType = {
  title: string;
  body: string;
};

const App: React.FC = () => {
  useApp();
  useAuthChecked();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { keycloak } = useKeycloak();
  const { loading } = useAppBoot();
  const [api, contextHolder] = notification.useNotification();

  // const { authChecked } = useAuthChecked();
  const { loading: getMeLoading, siteRole } = useAppSelector(
    (state) => state.login
  );
  const isSA = stringToArray(location.pathname, "/")?.[0] === "sa";

  // console.log("Token Check");

  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((registration) => {
          console.log(
            "Service Worker registered with scope: ",
            registration.scope
          );
        })
        .catch((error) => {
          console.error("Service Worker registration failed: ", error);
        });
    });
  }

  // Notification with firebase
  useEffect(() => {
    if (keycloak?.token)
      generateToken().then((el) => {
        dispatch(
          addMobileTokenAsync({
            data: {
              mobileToken: el,
              token: keycloak?.token,
            },
          })
        );
        // dispatch(setNotificationToken(el));
      });

    const openNotification = (msg: MSGType) => {
      if (msg?.title !== "map") {
        api.open({
          message: msg?.title,
          description: msg?.body,
          icon: <NotificationIcon />,
        });
      }
    };

    // const playSound = () => {
    //   const audio = new Audio(notificationSound);
    //   audio.play();
    // };

    onMessage(messaging, (payload) => {
      const notification = payload?.notification as MSGType;
      openNotification(notification);
      // playSound();
    });
  }, [api, dispatch, keycloak?.token]);

  // if (authChecked) {
  //   return <Loader />;
  // }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: isSA ? "#2497EB" : "#33AB29",
          colorPrimaryText: "#3D4A5C",
          fontFamily: "Inter",
        },
        components: {
          Button: {
            colorPrimary: "#0d0d0d",
            colorPrimaryHover: "#222",
          },
          Timeline: {
            tailColor: "#33AB29",
            itemPaddingBottom: 18,
          },
        },
      }}
    >
      <AntApp>
        {/* <Router> */}
        {contextHolder}
        {loading || getMeLoading ? (
          <Loader />
        ) : (
          <Routes>
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    siteRole === "NP"
                      ? "/np/dashboard"
                      : siteRole === "SA"
                      ? "/sa/dashboard"
                      : siteRole === "VENDOR"
                      ? "/vendor/dashboard"
                      : siteRole === "CLUSTER"
                      ? "/cluster-ops/dashboard"
                      : siteRole === "DIRECTOROPS"
                      ? "/director-ops/dashboard"
                      : siteRole === "ENERGYOPS"
                      ? "/energy-ops/dashboard"
                      : siteRole === "FINANCE"
                      ? "/finance/dashboard"
                      : siteRole === "GROPS"
                      ? "/gr-ops/dashboard"
                      : siteRole === "REGIONAL"
                      ? "/regional/dashboard"
                      : siteRole === "SCM"
                      ? "/scm/dashboard"
                      : "/login"
                  }
                />
              }
            />

            {/* public routers */}
            {publicRouters.map(({ path, element: Element }, idx) => (
              <Route
                path={path}
                key={idx + path}
                element={
                  <Suspense fallback={<Loader />}>
                    <Element />
                  </Suspense>
                }
              />
            ))}

            {/* Auth routers */}
            {authRouters.map(({ path, element: Element, roles }, idx) => {
              const np = roles?.length && roles?.includes("NP");
              const sa = roles?.length && roles?.includes("SA");
              const vendor = roles?.length && roles?.includes("VENDOR");
              const cluster = roles?.length && roles?.includes("CLUSTER");
              const energyOps = roles?.length && roles?.includes("ENERGYOPS");
              const direcctorOps =
                roles?.length && roles?.includes("DIRECTOROPS");
              const grOps = roles?.length && roles?.includes("GROPS");
              const regional = roles?.length && roles?.includes("REGIONAL");
              const finance = roles?.length && roles?.includes("FINANCE");
              const scm = roles?.length && roles?.includes("SCM");

              let pathLink = "";
              if (np) pathLink = "/np";
              else if (sa) pathLink = "/sa";
              else if (vendor) pathLink = "/vendor";
              else if (cluster) pathLink = "/cluster-ops";
              else if (energyOps) pathLink = "/energy-ops";
              else if (direcctorOps) pathLink = "/director-ops";
              else if (grOps) pathLink = "/gr-ops";
              else if (regional) pathLink = "/regional";
              else if (finance) pathLink = "/finance";
              else if (scm) pathLink = "/scm";

              if (pathLink) {
                return (
                  <Route
                    path={pathLink}
                    element={<SidebarLayout />}
                    key={idx + path}
                  >
                    <Route
                      path={path}
                      element={
                        <AuthMiddleware roles={roles}>
                          <Suspense fallback={<Loader />}>
                            <Element />
                          </Suspense>
                        </AuthMiddleware>
                      }
                    />
                  </Route>
                );
              }

              return null;
            })}
          </Routes>
        )}

        {/* </Router> */}
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
